import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Button, Modal, Field, TextArea, Select, Switch } from "../../../shared/ui";
import {
  listBroadcasts,
  createBroadcast,
  updateBroadcast,
  deleteBroadcast,
  type Broadcast,
} from "../../../features/admin/api/adminApi";
import { PageHeader, shortDate } from "./_shared";

export default function AdminBroadcasts() {
  const [items, setItems] = useState<Broadcast[] | null>(null);
  const [creating, setCreating] = useState(false);

  const load = () => listBroadcasts().then(setItems).catch(() => undefined);
  useEffect(() => { void load(); }, []);

  const toggle = async (b: Broadcast) => { await updateBroadcast(b._id, { active: !b.active }); await load(); };
  const remove = async (b: Broadcast) => { if (confirm(`Delete "${b.title}"?`)) { await deleteBroadcast(b._id); await load(); } };

  return (
    <>
      <PageHeader
        title="Broadcasts"
        subtitle="In-app announcements shown to all users"
        actions={<Button variant="primary" size="sm" onClick={() => setCreating(true)}><Plus size={14} /> New broadcast</Button>}
      />
      <div className="p-4 sm:p-6">
        {!items ? (
          <div className="flex h-40 items-center justify-center text-ink-400"><Loader2 className="animate-spin" /></div>
        ) : items.length === 0 ? (
          <p className="text-[12.5px] text-ink-400">No broadcasts yet.</p>
        ) : (
          <div className="space-y-3">
            {items.map((b) => (
              <div key={b._id} className="surface-card flex items-start justify-between gap-3 p-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${b.active ? "bg-emerald" : "bg-ink-300"}`} />
                    <p className="font-semibold text-ink-900">{b.title}</p>
                    <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[10px] font-medium capitalize text-ink-500">{b.level}</span>
                  </div>
                  <p className="mt-1 text-[12.5px] text-ink-600">{b.message}</p>
                  <p className="mt-1 text-[11px] text-ink-400">
                    {b.active ? "Live" : "Inactive"} · created {shortDate(b.createdAt)}{b.expiresAt ? ` · expires ${shortDate(b.expiresAt)}` : ""}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Switch checked={b.active} onChange={() => toggle(b)} />
                  <button onClick={() => remove(b)} className="rounded-lg p-1.5 text-ink-400 hover:text-rose"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {creating && <BroadcastModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </>
  );
}

function BroadcastModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [level, setLevel] = useState<"info" | "warn" | "success">("info");
  const [actionUrl, setActionUrl] = useState("");
  const [actionLabel, setActionLabel] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!title.trim() || !message.trim()) { setErr("Title and message are required"); return; }
    setSaving(true);
    setErr(null);
    try {
      await createBroadcast({ title, message, level, actionUrl: actionUrl || undefined, actionLabel: actionLabel || undefined, active });
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="New broadcast"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" disabled={saving} onClick={save}>{saving ? <Loader2 size={13} className="animate-spin" /> : null} Publish</Button>
        </>
      }
    >
      <div className="space-y-3">
        {err && <p className="rounded-lg bg-rose/10 px-3 py-2 text-[12.5px] text-rose">{err}</p>}
        <Field label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <TextArea label="Message" value={message} onChange={(e) => setMessage(e.target.value)} rows={2} />
        <Select label="Level" value={level} onChange={(e) => setLevel(e.target.value as "info" | "warn" | "success")}
          options={[{ value: "info", label: "Info" }, { value: "warn", label: "Warning" }, { value: "success", label: "Success" }]} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Action URL (optional)" value={actionUrl} onChange={(e) => setActionUrl(e.target.value)} />
          <Field label="Action label (optional)" value={actionLabel} onChange={(e) => setActionLabel(e.target.value)} />
        </div>
        <Switch label="Active immediately" checked={active} onChange={(e) => setActive(e.target.checked)} />
      </div>
    </Modal>
  );
}
